import React, { useMemo } from 'react';
import { useOptimizationStore } from '../store/optimizationStore';
import { sortMetaPromptKeys } from '../utils/sortMetaPromptKeys';

interface AgentSelectorProps {
  variant?: 'initial' | 'optimized';
  onAgentClick?: (agentType: 'coordinator' | 'worker' | 'meta', agentIndex?: number, metaPromptKey?: string) => void;
  selectedAgent?: { type: 'coordinator' | 'worker' | 'meta'; index?: number; metaPromptKey?: string } | null;
  workersCount?: number;
  iterationIndex?: number; // Для optimized варианта
  isConnector?: boolean; // Для connector страницы
}

const AgentSelector: React.FC<AgentSelectorProps> = ({ 
  variant = 'initial', 
  onAgentClick, 
  selectedAgent,
  workersCount: propWorkersCount,
  iterationIndex,
  isConnector = false
}) => {
  const { 
    workers, 
    multiAgentIterationResults,
    multiAgentSelectedIterationIndex,
    connectorWorkers,
    connectorMultiAgentIterationResults,
    connectorMultiAgentSelectedIterationIndex,
    connectorMetaPrompts,
    coordinatorName,
    connectorCoordinatorName,
  } = useOptimizationStore();
  
  // Выбираем правильные данные в зависимости от типа страницы
  const currentWorkersFromStore = isConnector ? connectorWorkers : workers;
  const currentIterationResults = isConnector ? connectorMultiAgentIterationResults : multiAgentIterationResults;
  const currentSelectedIterationIndex = isConnector ? connectorMultiAgentSelectedIterationIndex : multiAgentSelectedIterationIndex;

  // Получаем baseline result для initial варианта
  const baselineResult = useMemo(() => {
    if (variant === 'initial') {
      return currentIterationResults.find(r => r.seq === 0 || r.iteration === 0);
    }
    return undefined;
  }, [currentIterationResults, variant]);

  // Определяем, какие данные использовать
  const currentIterationIndex = variant === 'optimized' 
    ? (iterationIndex !== undefined ? iterationIndex : currentSelectedIterationIndex)
    : undefined;

  // Получаем оптимизированные результаты (seq > 0)
  const optimizedResults = useMemo(() => {
    return currentIterationResults
      .filter(r => {
        const seq = r.seq !== undefined ? r.seq : r.iteration;
        return seq !== 0 && seq !== undefined;
      })
      .sort((a, b) => {
        const seqA = a.seq !== undefined ? a.seq : a.iteration;
        const seqB = b.seq !== undefined ? b.seq : b.iteration;
        return seqA - seqB;
      });
  }, [currentIterationResults]);

  const currentResult = variant === 'optimized' && currentIterationIndex !== undefined
    ? optimizedResults[currentIterationIndex]
    : undefined;

  // Получаем workers из результата или из store
  // Для optimized варианта используем workers из результата, но сопоставляем с workers из store для определения режима
  const currentWorkers = variant === 'optimized' && currentResult?.["worker-agents"]
    ? currentResult["worker-agents"]
    : currentWorkersFromStore || [];
  
  // Создаем маппинг workers из результата к workers из store по индексу
  // Приоритет для mode: сначала из результата, потом из store по имени, потом по индексу, потом default
  const workersWithMode = useMemo(() => {
    if (variant === 'optimized' && currentResult?.["worker-agents"]) {
      // Для optimized варианта: показываем ВСЕХ workers из результата
      // Приоритет mode: 1) из resultWorker.mode, 2) из store по имени, 3) из store по индексу, 4) default
      return currentResult["worker-agents"].map((resultWorker: any, index: number) => {
        // Сначала проверяем mode в самом resultWorker
        let workerMode = resultWorker.mode;
        
        // Если mode нет в resultWorker, ищем в store по имени
        if (!workerMode && currentWorkersFromStore) {
          const storeWorkerByName = currentWorkersFromStore.find((w: any) => w.name === resultWorker.name);
          if (storeWorkerByName) {
            workerMode = (storeWorkerByName as any).mode;
          }
        }
        
        // Если все еще нет, берем из store по индексу
        if (!workerMode && currentWorkersFromStore && currentWorkersFromStore.length > index) {
          workerMode = (currentWorkersFromStore[index] as any).mode;
        }
        
        // Fallback к synchronous
        workerMode = workerMode || 'synchronous';
        
        return {
          ...resultWorker,
          originalIndex: index,
          name: resultWorker.name || `Worker ${index + 1}`,
          mode: workerMode
        };
      });
    }
    // Для initial варианта используем ВСЕХ workers
    // Объединяем workers из baselineResult и workers из store, чтобы показать всех
    if (variant === 'initial') {
      const baselineWorkers = baselineResult?.["worker-agents"] || [];
      const storeWorkers = currentWorkersFromStore || [];
      
      // Берем максимум из обоих источников
      const maxLength = Math.max(baselineWorkers.length, storeWorkers.length);
      
      return Array.from({ length: maxLength }, (_, index: number) => {
        const baselineWorker = baselineWorkers[index];
        const storeWorker = storeWorkers[index];
        
        // Приоритет: baselineWorker, если есть, иначе storeWorker
        const worker = baselineWorker || storeWorker;
        
        if (!worker) {
          // Если нет ни baseline, ни store worker, создаем пустой объект
          return {
            originalIndex: index,
            name: `Worker ${index + 1}`,
            mode: 'synchronous' as const
          };
        }
        
        // Определяем mode агента:
        // 1. Если baselineResult существует (seq 0 получен), используем mode из baselineWorker
        // 2. Иначе (до получения seq 0) используем mode из storeWorker (конфигуратора)
        // 3. Fallback к synchronous
        let workerMode: 'synchronous' | 'asynchronous' = 'synchronous';
        if (baselineResult && baselineWorker) {
          // seq 0 получен, используем mode из baselineWorker
          workerMode = (baselineWorker as any)?.mode || 'synchronous';
        } else if (storeWorker) {
          // До получения seq 0 используем mode из конфигуратора (storeWorker)
          workerMode = (storeWorker as any)?.mode || 'synchronous';
        }
        
        return {
          ...worker,
          originalIndex: index,
          name: worker.name || storeWorker?.name || `Worker ${index + 1}`,
          mode: workerMode
        };
      });
    }
    // Fallback
    return currentWorkers.map((worker: any, index: number) => ({
      ...worker,
      originalIndex: index,
      name: worker.name || `Worker ${index + 1}`,
      mode: (worker as any).mode || 'synchronous'
    }));
  }, [variant, currentResult, currentWorkers, workers, baselineResult]);

  // Разделяем workers на sync и async
  // Используем workersWithMode, который содержит правильные режимы
  const syncWorkers = useMemo(() => {
    return workersWithMode.filter((worker: any) => worker.mode === 'synchronous');
  }, [workersWithMode]);

  const asyncWorkers = useMemo(() => {
    return workersWithMode.filter((worker: any) => worker.mode === 'asynchronous');
  }, [workersWithMode]);

  const metaPrompts = useMemo(() => {
    // Для optimized варианта берем из результатов
    if (variant === 'optimized' && currentResult) {
      return currentResult["meta_prompts"] || currentResult["metaPrompts"];
    }
    
    // Для initial варианта:
    // - Если это connector страница, используем connectorMetaPrompts из конфигуратора
    // - Иначе используем baselineResult
    if (variant === 'initial') {
      if (isConnector && connectorMetaPrompts) {
        return connectorMetaPrompts;
      }
      if (baselineResult) {
        return baselineResult["meta_prompts"] || baselineResult["metaPrompts"];
      }
    }
    
    return undefined;
  }, [variant, currentResult, baselineResult, isConnector, connectorMetaPrompts]);

  // Показываем только те метапромпты, которые есть в результатах (не пустые), в стабильном порядке
  const metaPromptKeys = useMemo(() => {
    if (metaPrompts && typeof metaPrompts === 'object') {
      const keys = Object.keys(metaPrompts).filter((key) => {
        const value = (metaPrompts as Record<string, any>)[key];
        return value && typeof value === 'string' && value.trim().length > 0;
      });
      return sortMetaPromptKeys(keys);
    }
    return [];
  }, [metaPrompts]);

  const coordinatorDisplayName = useMemo(() => {
    if (variant === 'optimized' && currentResult?.['master-agent']?.name) {
      const n = String(currentResult['master-agent'].name).trim();
      if (n) return n;
    }
    if (variant === 'initial') {
      if (isConnector) {
        const n = (connectorCoordinatorName || '').trim();
        return n || 'Координатор';
      }
      const fromBaseline = baselineResult?.['master-agent']?.name;
      if (fromBaseline != null && String(fromBaseline).trim()) {
        return String(fromBaseline).trim();
      }
      const n = (coordinatorName || '').trim();
      return n || 'Координатор';
    }
    const n = (coordinatorName || '').trim();
    return n || 'Координатор';
  }, [
    variant,
    currentResult,
    isConnector,
    connectorCoordinatorName,
    baselineResult,
    coordinatorName,
  ]);

  const isAgentSelected = (type: 'coordinator' | 'worker' | 'meta', index?: number, metaPromptKey?: string) => {
    if (!selectedAgent) return false;
    if (selectedAgent.type !== type) return false;
    if (type === 'worker' && selectedAgent.index !== index) return false;
    if (type === 'meta' && selectedAgent.metaPromptKey !== metaPromptKey) return false;
    return true;
  };

  const handleAgentClick = (type: 'coordinator' | 'worker' | 'meta', index?: number, metaPromptKey?: string) => {
    if (onAgentClick) {
      onAgentClick(type, index, metaPromptKey);
    }
  };

  return (
    <div className="agent-selector">
      <div className="agent-selector-header">
        <h4 className="agent-selector-title">Выбор агента</h4>
      </div>
      
      <div className="agent-selector-content">
        {/* Coordinator */}
        <div className="agent-group">
          <div className="agent-group-header">
            <span className="agent-group-title">Координатор</span>
          </div>
          <div 
            className={`agent-item ${isAgentSelected('coordinator') ? 'selected' : ''}`}
            onClick={() => handleAgentClick('coordinator')}
          >
            <i className="fas fa-robot"></i>
            <span>{coordinatorDisplayName}</span>
          </div>
        </div>

        {/* Workers - разделяем на Synchronous и Asynchronous */}
        {syncWorkers.length > 0 && (
          <div className="agent-group">
            <div className="agent-group-header">
              <span className="agent-group-title">Исполнители (синхронные)</span>
            </div>
            {syncWorkers.map((worker: any, idx: number) => (
              <div
                key={`sync-${idx}`}
                className={`agent-item ${isAgentSelected('worker', worker.originalIndex) ? 'selected' : ''}`}
                onClick={() => handleAgentClick('worker', worker.originalIndex)}
              >
                <i className="fas fa-robot"></i>
                <span>{worker.name || `Worker ${idx + 1}`}</span>
              </div>
            ))}
          </div>
        )}

        {asyncWorkers.length > 0 && (
          <div className="agent-group">
            <div className="agent-group-header">
              <span className="agent-group-title">Исполнители (асинхронные)</span>
            </div>
            {asyncWorkers.map((worker: any, idx: number) => (
              <div
                key={`async-${idx}`}
                className={`agent-item ${isAgentSelected('worker', worker.originalIndex) ? 'selected' : ''}`}
                onClick={() => handleAgentClick('worker', worker.originalIndex)}
              >
                <i className="fas fa-robot"></i>
                <span>{worker.name || `Worker ${idx + 1}`}</span>
              </div>
            ))}
          </div>
        )}

        {/* Meta Prompts */}
        {metaPromptKeys.length > 0 && (
          <div className="agent-group">
            <div className="agent-group-header">
              <span className="agent-group-title">Мета-промпты</span>
            </div>
            {metaPromptKeys.map((key) => (
              <div
                key={key}
                className={`agent-item ${isAgentSelected('meta', undefined, key) ? 'selected' : ''}`}
                onClick={() => handleAgentClick('meta', undefined, key)}
              >
                <i className="fas fa-code"></i>
                <span>{key.replace(/_/g, ' ')}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentSelector;
